import { Link } from "react-router-dom";
import { Bird, Bug, Shield, Phone, Mail, MapPin, ChevronRight, Award, Check, Clock, Star, MessageSquare } from "lucide-react";
import { Button } from "../components/ui/button";
import { Header } from "../components/Layout";

// =============================================================================
// SEO HEADINGS CONFIG
// -----------------------------------------------------------------------------
// All homepage heading text is centralised here so future rebalancing (e.g.
// leaning more Brisbane vs. Ipswich, or A/B testing) is a one-file edit.
// Every heading pairs "Ipswich" with "Brisbane" so both markets are always
// represented, while keeping the primary keyword ("Pest Control Ipswich") near
// the front of the H1 for SEO.
// =============================================================================
const HEADINGS = {
  h1: {
    prefix: "Pest Control ",
    highlight: "Ipswich & Brisbane",
    suffix: " — Your Local Experts",
  },
  h1Subhead: "Professional pest control, bird proofing and termite treatment for homes and businesses across Ipswich, Brisbane and surrounding suburbs of South East Queensland.",
  services: {
    prefix: "Our Pest Control Services in ",
    highlight: "Ipswich & Brisbane",
  },
  whyChoose: {
    prefix: "Why Choose ",
    highlight: "Pest Free",
    suffix: " for Ipswich Pest Control",
  },
  serviceAreas: {
    prefix: "Servicing ",
    highlight: "Ipswich, Brisbane & SEQ",
  },
  serviceAreasSubhead: "Professional pest control across South East Queensland",
  reviews: {
    prefix: "What ",
    highlight: "Ipswich & Brisbane",
    suffix: " Customers Say",
  },
  reviewsSubhead: "Real reviews from Ipswich & Brisbane homeowners",
  faq: {
    prefix: "Pest Control ",
    highlight: "FAQs",
    suffix: " — Ipswich & Brisbane",
  },
  cta: {
    prefix: "Need Pest Control in ",
    highlight: "Ipswich or Brisbane",
    suffix: "?",
  },
  // Services grid card titles — subtle keyword injection
  serviceCards: {
    pest: "General Pest & Cockroach Treatment Ipswich",
    birds: "Bird & Pigeon Control Ipswich",
    termites: "Termite Inspection & Treatment Ipswich",
  },
  endOfLease: {
    heading: "End of Lease Pest Control Ipswich",
    body: "Moving out of a rental? We provide same-day end-of-lease pest treatments with a bond-back guarantee and receipt for your property manager. Accepted by all Ipswich real estate agents.",
    ctaLabel: "Learn About End of Lease Treatments",
  },
};

// SEO Component - Uses React 19 native metadata hoisting (title/meta auto-move to <head>)
const PageSEO = () => (
  <>
    <title>Pest Control Ipswich & Brisbane | Bird Proofing & Termite Treatment</title>
    <meta name="google-site-verification" content="G_elVeoyxHDbII68qEV6dEhLDSjz_ceOBByWO5gdUWA" />
    <meta name="description" content="Professional pest control, solar panel bird proofing and termite treatment across Ipswich & Brisbane. QBCC Licensed. Call (07) 3708 1422 for a free quote." />
    <meta name="keywords" content="pest control Brisbane, pest control Ipswich, termite inspection, bird proofing Brisbane, cockroach control, ant treatment" />
    <link rel="canonical" href="https://pestfreebrisbane.com.au/" />
    <meta property="og:title" content="Pest Free Ipswich & Brisbane | Pest Control, Bird Proofing & Termite Treatment" />
    <meta property="og:description" content="Professional pest control services in Ipswich & Brisbane. Call (07) 3708 1422 for a free quote." />
    <meta property="og:url" content="https://pestfreebrisbane.com.au/" />
  </>
);

// Service Card Component
const ServiceCard = ({ icon: Icon, iconImage, title, description, color, link, services }) => {
  const colorClasses = {
    blue: {
      bg: "bg-[#5BC0DE]/10",
      border: "border-[#5BC0DE]/30 hover:border-[#5BC0DE]",
      icon: "text-[#5BC0DE]",
      iconBg: "bg-[#5BC0DE]/20",
      button: "bg-[#5BC0DE] hover:bg-[#4AA8C4]"
    },
    orange: {
      bg: "bg-[#D2691E]/10",
      border: "border-[#D2691E]/30 hover:border-[#D2691E]",
      icon: "text-[#D2691E]",
      iconBg: "bg-[#D2691E]/20",
      button: "bg-[#D2691E] hover:bg-[#B8581A]"
    },
    red: {
      bg: "bg-[#E74C3C]/10",
      border: "border-[#E74C3C]/30 hover:border-[#E74C3C]",
      icon: "text-[#E74C3C]",
      iconBg: "bg-[#E74C3C]/20",
      button: "bg-[#E74C3C] hover:bg-[#C0392B]"
    }
  };

  const classes = colorClasses[color];

  return (
    <Link 
      to={link}
      className={`group block ${classes.bg} ${classes.border} border-2 rounded-3xl p-8 transition-all duration-300 hover:shadow-xl hover:-translate-y-1`}
      data-testid={`service-card-${color}`}
    >
      {iconImage ? (
        <div className="w-16 h-16 rounded-full overflow-hidden mb-6">
          <img src={iconImage} alt={title} className="w-full h-full object-cover" />
        </div>
      ) : (
        <div className={`inline-flex p-4 rounded-2xl ${classes.iconBg} mb-6`}>
          <Icon className={`w-10 h-10 ${classes.icon}`} />
        </div>
      )}
      
      <h3 className="text-2xl font-heading font-bold text-slate-900 mb-3">{title}</h3>
      <p className="text-slate-600 mb-4 leading-relaxed">{description}</p>
      
      <ul className="space-y-2 mb-6">
        {services.map((service, i) => (
          <li key={i} className="flex items-center gap-2 text-slate-700">
            <Check className={`w-4 h-4 ${classes.icon}`} />
            <span className="text-sm">{service}</span>
          </li>
        ))}
      </ul>
      
      <div className={`inline-flex items-center gap-2 ${classes.button} text-white px-6 py-3 rounded-full font-semibold text-base transition-all group-hover:gap-3`}>
        Get a Quote
        <ChevronRight className="w-5 h-5" />
      </div>
    </Link>
  );
};

export default function HomePage() {
  return (
    <div className="min-h-screen">
      <PageSEO />
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 overflow-hidden" data-testid="home-hero">
        {/* Background image with overlay */}
        <div className="absolute inset-0">
          <img src="https://customer-assets.emergentagent.com/job_fd293a20-78a6-4d86-9d01-01576b2818e2/artifacts/rszkan9y_IMG_2133.jpeg" alt="" className="w-full h-full object-cover scale-110" style={{ objectPosition: '20% center' }} />
          <div className="absolute inset-0 bg-black/60" />
        </div>
        
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-[#7ABC32]/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#7ABC32]/5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            {/* Small badge */}
            <div className="inline-flex items-center gap-2 bg-[#7ABC32]/20 rounded-full px-4 py-2 mb-8">
              <span className="w-3 h-3 bg-[#7ABC32] rounded-full animate-pulse shadow-[0_0_12px_3px_rgba(122,188,50,0.6)]" />
              <span className="text-sm font-medium text-[#7ABC32]">Serving Ipswich & Brisbane</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white mb-6 leading-tight">
              {HEADINGS.h1.prefix}<span className="text-[#7ABC32]">{HEADINGS.h1.highlight}</span>{HEADINGS.h1.suffix}
            </h1>
            
            <p className="text-lg md:text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed mb-8">
              {HEADINGS.h1Subhead}
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <a 
                href="tel:0737081422"
                className="inline-flex items-center justify-center gap-2 bg-[#7ABC32] hover:bg-[#6AAD28] text-white px-8 py-4 rounded-full text-lg font-semibold transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                data-testid="hero-call-btn"
              >
                <Phone className="w-5 h-5" />
                Call (07) 3708 1422
              </a>
            </div>
            
            {/* Service Cards - 3 in line */}
            <div className="grid grid-cols-3 gap-3 sm:gap-5 mb-12">
              <Link 
                to="/pests"
                className="group block bg-[#E74C3C]/10 border-2 border-[#E74C3C]/30 hover:border-[#E74C3C] rounded-2xl p-4 sm:p-6 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 text-center"
                data-testid="hero-pests-link"
              >
                <div className="w-14 h-14 mx-auto mb-3 rounded-full overflow-hidden shadow-lg">
                  <img src="https://customer-assets.emergentagent.com/job_e49af65e-59bb-4d0b-86e9-ec5fdd6bec61/artifacts/w48t20wm_IMG_1874.png" alt="Pest Control" className="w-full h-full object-cover" />
                </div>
                <h3 className="text-white font-bold text-base sm:text-lg mb-1">Pest Control</h3>
                <p className="text-slate-400 text-xs sm:text-sm mb-2 hidden sm:block">Cockroaches, ants, spiders, rodents & more</p>
                <span className="text-[#7ABC32] font-semibold text-xs sm:text-sm">From $165 — Get Quote →</span>
              </Link>
              
              <Link 
                to="/birds"
                className="group block bg-[#5BC0DE]/10 border-2 border-[#5BC0DE]/30 hover:border-[#5BC0DE] rounded-2xl p-4 sm:p-6 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 text-center"
                data-testid="hero-birds-link"
              >
                <div className="w-14 h-14 mx-auto mb-3 rounded-full overflow-hidden shadow-lg">
                  <img src="https://customer-assets.emergentagent.com/job_e49af65e-59bb-4d0b-86e9-ec5fdd6bec61/artifacts/h01882qk_IMG_1877.png" alt="Bird Control" className="w-full h-full object-cover" />
                </div>
                <h3 className="text-white font-bold text-base sm:text-lg mb-1">Bird Proofing</h3>
                <p className="text-slate-400 text-xs sm:text-sm mb-2 hidden sm:block">Solar panel mesh, pigeon removal & cleanup</p>
                <span className="text-[#5BC0DE] font-semibold text-xs sm:text-sm">10yr Warranty — Get Quote →</span>
              </Link>
              
              <Link 
                to="/termites"
                className="group block bg-[#D2691E]/10 border-2 border-[#D2691E]/30 hover:border-[#D2691E] rounded-2xl p-4 sm:p-6 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 text-center"
                data-testid="hero-termites-link"
              >
                <div className="w-14 h-14 mx-auto mb-3 rounded-full overflow-hidden shadow-lg">
                  <img src="https://customer-assets.emergentagent.com/job_e49af65e-59bb-4d0b-86e9-ec5fdd6bec61/artifacts/fcafjpg8_IMG_1873.png" alt="Termite Control" className="w-full h-full object-cover" />
                </div>
                <h3 className="text-white font-bold text-base sm:text-lg mb-1">Termites</h3>
                <p className="text-slate-400 text-xs sm:text-sm mb-2 hidden sm:block">Inspections, barriers & baiting systems</p>
                <span className="text-[#D2691E] font-semibold text-xs sm:text-sm">Book Inspection →</span>
              </Link>
            </div>
            
            {/* Trust indicators */}
            <div className="flex flex-wrap justify-center gap-6 text-sm text-slate-400">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#7ABC32]" />
                <span>QBCC Licensed</span>
              </div>
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5 text-[#7ABC32]" />
                <span>Fully Insured</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#7ABC32]" />
                <span>Fast Response</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#7ABC32]" />
                <span>Locally Owned</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sticky-friendly Call Bar */}
      <section className="bg-[#7ABC32] py-4" data-testid="call-bar">
        <div className="max-w-4xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6">
          <p className="text-white font-semibold text-base sm:text-lg">Not sure what you need? We'll help you out.</p>
          <a href="tel:0737081422" className="inline-flex items-center gap-2 bg-white text-[#1a1a2e] font-bold px-6 py-3 rounded-full text-base sm:text-lg hover:bg-slate-100 transition-all shadow-md" data-testid="call-bar-phone">
            <Phone className="w-5 h-5" /> Call (07) 3708 1422
          </a>
        </div>
      </section>
      
      {/* Services Section */}
      <section className="py-20 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-slate-900 mb-4">
              {HEADINGS.services.prefix}<span className="text-[#7ABC32]">{HEADINGS.services.highlight}</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Comprehensive pest management for homes and businesses across Ipswich, Brisbane and surrounding suburbs. From general pest control and cockroach treatment to termite inspections and bird proofing — we've got you covered.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <ServiceCard 
              iconImage="https://customer-assets.emergentagent.com/job_e49af65e-59bb-4d0b-86e9-ec5fdd6bec61/artifacts/w48t20wm_IMG_1874.png"
              title="General Pest Control"
              description="Complete pest management for cockroaches, ants, spiders, bed bugs, rodents and more."
              color="red"
              link="/pests"
              services={[
                "Cockroaches & ants",
                "Spiders & rodents",
                "Bed bugs",
                "Mosquito fogging"
              ]}
            />
            
            <ServiceCard 
              iconImage="https://customer-assets.emergentagent.com/job_e49af65e-59bb-4d0b-86e9-ec5fdd6bec61/artifacts/h01882qk_IMG_1877.png"
              title={HEADINGS.serviceCards.birds}
              description="Professional solar panel bird proofing and pigeon removal. Stop birds nesting under your panels."
              color="blue"
              link="/birds"
              services={[
                "Solar proofing mesh",
                "Bird netting & spiking",
                "Cat/pet enclosures",
                "Nest removal & cleanup",
                "Bird mite treatment"
              ]}
            />
            
            <ServiceCard 
              iconImage="https://customer-assets.emergentagent.com/job_e49af65e-59bb-4d0b-86e9-ec5fdd6bec61/artifacts/fcafjpg8_IMG_1873.png"
              title={HEADINGS.serviceCards.termites}
              description="Protect your biggest investment with professional termite inspections and treatment systems."
              color="orange"
              link="/termites"
              services={[
                "Termite inspections",
                "Active treatments",
                "Chemical barriers",
                "Baiting systems",
                "Pre-construction treatments"
              ]}
            />
          </div>
        </div>
      </section>
      
      {/* Why Choose Us Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-4">
              {HEADINGS.whyChoose.prefix}<span className="text-[#7ABC32]">{HEADINGS.whyChoose.highlight}</span>{HEADINGS.whyChoose.suffix}
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Shield, title: "Licensed & Insured", desc: "QBCC licensed with full insurance coverage" },
              { icon: MapPin, title: "Local Experts", desc: "We know Ipswich & Brisbane pest problems" },
              { icon: Phone, title: "Fast Response", desc: "2-hour response time guaranteed" },
              { icon: Award, title: "Owner Operated", desc: "Support locals who support locals" }
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 text-center shadow-sm border border-slate-100">
                <div className="inline-flex p-3 bg-[#7ABC32]/10 rounded-xl mb-4">
                  <item.icon className="w-8 h-8 text-[#7ABC32]" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* End of Lease Strip - targeted SEO for "End of Lease Pest Control Ipswich" keyword */}
      <section className="py-10 bg-white" data-testid="end-of-lease-strip">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-[#7ABC32]/5 to-[#5A8F24]/10 border border-[#7ABC32]/20 rounded-2xl px-6 py-8 md:flex md:items-center md:justify-between md:gap-8">
            <div className="mb-4 md:mb-0 md:flex-1">
              <h3 className="text-xl md:text-2xl font-heading font-bold text-slate-900 mb-2">
                {HEADINGS.endOfLease.heading}
              </h3>
              <p className="text-slate-600 leading-relaxed">
                {HEADINGS.endOfLease.body}
              </p>
            </div>
            <Link
              to="/end-of-lease-pest-control-ipswich"
              data-testid="end-of-lease-link"
              className="inline-flex items-center gap-2 bg-[#7ABC32] hover:bg-[#6AAD28] text-white px-5 py-3 rounded-full font-semibold whitespace-nowrap shadow-sm hover:shadow-md transition-all"
            >
              {HEADINGS.endOfLease.ctaLabel}
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
      
      {/* Service Areas - Grouped Regional Coverage */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-4">
              {HEADINGS.serviceAreas.prefix}<span className="text-[#7ABC32]">{HEADINGS.serviceAreas.highlight}</span>
            </h2>
            <p className="text-lg text-slate-600">{HEADINGS.serviceAreasSubhead}</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              {
                name: "Ipswich Region",
                suburbs: "Ipswich CBD, Brassall, Booval, Bundamba, Redbank Plains, Goodna, Springfield, Springfield Lakes, Ripley, Deebing Heights",
                link: "/ipswich"
              },
              {
                name: "Lockyer Valley",
                suburbs: "Gatton, Laidley, Plainland, Withcott, Helidon, Forest Hill, Grantham",
                link: "/lockyer-valley"
              },
              {
                name: "Brisbane South",
                suburbs: "Mt Gravatt, Sunnybank, Carindale, Wishart, Upper Mt Gravatt, Eight Mile Plains, Rochedale",
                link: "/brisbane-south"
              },
              {
                name: "Brisbane West",
                suburbs: "Indooroopilly, Kenmore, Chapel Hill, Jindalee, Mt Ommaney, Centenary, Sherwood, Graceville",
                link: "/brisbane-west"
              },
              {
                name: "Logan Corridor",
                suburbs: "Logan Central, Woodridge, Browns Plains, Marsden, Crestmead, Beenleigh, Eagleby, Waterford",
                link: "/logan"
              }
            ].map((region, i) => {
              const content = (
                <>
                  <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-[#7ABC32]" />
                    {region.name}
                    {region.link && <ChevronRight className="w-4 h-4 text-[#7ABC32] ml-auto" />}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{region.suburbs}</p>
                </>
              );
              return region.link ? (
                <Link key={i} to={region.link} className="bg-slate-50 rounded-xl p-5 border border-slate-100 hover:border-[#7ABC32]/30 hover:shadow-md transition-all cursor-pointer" data-testid={`region-tile-${i}`}>
                  {content}
                </Link>
              ) : (
                <div key={i} className="bg-slate-50 rounded-xl p-5 border border-slate-100 hover:border-[#7ABC32]/30 hover:shadow-md transition-all">
                  {content}
                </div>
              );
            })}
          </div>
        </div>
      </section>
      
      {/* Google Reviews Section */}
      <section className="py-20 bg-black" data-testid="reviews-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 mb-4">
              <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
              <span className="text-white font-medium">4.9 Stars on Google</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white mb-4">
              {HEADINGS.reviews.prefix}<span className="text-[#7ABC32]">{HEADINGS.reviews.highlight}</span>{HEADINGS.reviews.suffix}
            </h2>
            <p className="text-lg text-slate-400">{HEADINGS.reviewsSubhead}</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                name: "Tom Sheldon",
                location: "Google Review",
                rating: 5,
                text: "I had ants popping up all over my kitchen and bathroom. I contacted Pest Free, and they got back to me really quick. Job was done right — I have no more ants and Charlie was good to deal with.",
                service: "Pest Control"
              },
              {
                name: "Funky Monkey",
                location: "Google Review",
                rating: 5,
                text: "We called Pest Free and had someone out the same day in Mount Gravatt. Haven't had cockroaches or ants for 3 months now. Highly recommend",
                service: "Pest Control"
              },
              {
                name: "Laine Sunderland",
                location: "Google Review",
                rating: 5,
                text: "Couldn't recommend these guys enough! They provided a really efficient and quality service with the whole process only taking about an hour. Lot's of spiders and cockroaches out here in Albany Creek so it was amazing Jess was able to help us out. Have already booked a follow-up with them for the new year!",
                service: "Pest Control"
              },
              {
                name: "Dee Daniels",
                location: "Google Review",
                rating: 5,
                text: "Always a pleasure doing business with you Charlie, you are full of information and nothing is too much trouble",
                service: "Pest Control"
              },
              {
                name: "Jen Christiansen",
                location: "Google Review",
                rating: 4,
                text: "Connected with Pest Free Brisbane. Jess attended, removed birds nest from under solar panel and replaced some netting and added a few extra clips. Very professional, great communication and price. Would recommend them",
                service: "Bird Proofing"
              },
              {
                name: "Andrew Brown",
                location: "Google Review",
                rating: 5,
                text: "We used Pest Free Brisbane on a large commercial project for bird netting and bird spikes. The team was extremely well organised and professional throughout the job. They arrived with full safety documentation including SWMS, had all the correct equipment, and completed the works efficiently. The job was signed off with zero defects. Highly professional outfit — 5 stars.",
                service: "Bird Proofing"
              },
              {
                name: "Tessa Buzzo",
                location: "Google Review",
                rating: 5,
                text: "Pest Free Brisbane did a great job of the termite treatment at my house in Carina. Would definitely use them again!",
                service: "Termite Treatment"
              },
              {
                name: "Theresa Edwards",
                location: "Google Review",
                rating: 5,
                text: "These boys were great!! On time, friendly and affordable!! We had a bad problem with pigeons making their home under our solar panels. Highly recommend Pest Free!",
                service: "Bird Proofing"
              },
              {
                name: "Matt T",
                location: "Google Review",
                rating: 5,
                text: "Prompt and quick job!",
                service: "Pest Control"
              }
            ].map((review, i) => (
              <div key={i} className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(review.rating)].map((_, j) => (
                    <Star key={j} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-slate-300 mb-6 leading-relaxed">"{review.text}"</p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-white">{review.name}</p>
                    <p className="text-sm text-slate-400">{review.location}</p>
                  </div>
                  <span className="text-xs bg-[#7ABC32]/20 text-[#7ABC32] px-3 py-1 rounded-full font-medium">
                    {review.service}
                  </span>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-10 text-center">
            <a 
              href="https://accounts.google.com/v3/signin/identifier?continue=https%3A%2F%2Fwww.google.com%2Fmaps%2Fapi%2Fjs%2FReviewsService.CrossOriginAuthSuccess%3Fkey%3DAIzaSyBcv0QfUNUfBwo8pIGJ3teNCkaluSGUWus%26origin%3Dhttps%253A%252F%252Fwww.google.com&dsh=S638947591%3A1773275416521579&hl=en-GB&ifkv=ASfE1-oilfwfGHWnA6ln6MJ9aURI7CKzJySz7JC4bXqauIQ-kiy8fwNukji0dFvkhKJSCdoClIBX3Q&flowName=GlifWebSignIn&flowEntry=ServiceLogin" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-white hover:text-[#7ABC32] transition-colors font-medium"
            >
              <MessageSquare className="w-5 h-5" />
              Leave us a review on Google
              <ChevronRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="py-20 bg-white" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-slate-900 mb-4">
              {HEADINGS.faq.prefix}<span className="text-[#7ABC32]">{HEADINGS.faq.highlight}</span>{HEADINGS.faq.suffix}
            </h2>
            <p className="text-lg text-slate-600">Got questions? We've got answers.</p>
          </div>
          
          <div className="space-y-4">
            {[
              {
                q: "How quickly can you come out?",
                a: "We offer same-day service for urgent pest problems. For standard bookings, we typically schedule within 24-48 hours. Call us and we'll do our best to accommodate your timeline."
              },
              {
                q: "Are your treatments safe for pets and children?",
                a: "Yes! We use professional-grade products that are safe when applied correctly. We'll advise you on any precautions needed, but in most cases, you can return to normal activities within a few hours of treatment."
              },
              {
                q: "Do you offer warranties on your work?",
                a: "Absolutely. Our solar proofing mesh installations come with a 10-year warranty. General pest treatments include a service guarantee — if pests return within the warranty period, so do we, at no extra cost."
              },
              {
                q: "How much does pest control cost?",
                a: "Pricing varies depending on the type of pest, property size, and treatment required. We provide free quotes with no obligation. For bird proofing, you can use our online estimate calculator for instant pricing."
              },
              {
                q: "What areas do you service?",
                a: "We service Brisbane, Ipswich, Springfield, Logan, Redbank Plains, and surrounding suburbs in South East Queensland. Not sure if we cover your area? Give us a call!"
              },
              {
                q: "Are you licensed and insured?",
                a: "Yes. We hold a Queensland Pest Management licence (PMT: 012086924), QBCC licence as required for termite management (#15366887), Bird Pest Management licence, Working at Heights licence, and Elevated Work Platform (over 11m) licence. We're also fully insured for your peace of mind."
              }
            ].map((faq, i) => (
              <details key={i} className="group bg-slate-50 rounded-2xl border border-slate-200 overflow-hidden">
                <summary className="flex items-center justify-between p-6 cursor-pointer list-none font-semibold text-slate-900 hover:bg-slate-100 transition-colors">
                  <span>{faq.q}</span>
                  <ChevronRight className="w-5 h-5 text-slate-400 group-open:rotate-90 transition-transform" />
                </summary>
                <div className="px-6 pb-6 text-slate-600 leading-relaxed">
                  {faq.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#7ABC32] to-[#5A8F24]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white mb-6">
            {HEADINGS.cta.prefix}<span className="text-white/90 underline decoration-white/50">{HEADINGS.cta.highlight}</span>{HEADINGS.cta.suffix}
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Contact us today for a free consultation and quote.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:0737081422"
              className="inline-flex items-center justify-center gap-2 bg-white text-[#7ABC32] px-8 py-4 rounded-full text-lg font-semibold hover:bg-slate-100 transition-all shadow-lg"
            >
              <Phone className="w-5 h-5" />
              (07) 3708 1422
            </a>
            <a 
              href="mailto:charlie@pestfreebrisbane.com.au"
              className="inline-flex items-center justify-center gap-2 bg-white/20 text-white border-2 border-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-white/30 transition-all"
            >
              <Mail className="w-5 h-5" />
              Email Us
            </a>
          </div>
        </div>
      </section>
      
    </div>
  );
}
